import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import "../../../css/allFacility.css";
import "../../../css/hotels.css";
import { Default } from "../../layouts/default";
import { Link } from "react-router-dom";
import LazyImage from "../../common/LazyImage";
import hotelBannerBv from "../../../image/hotel-banner.png";

const API_URL = "https://admin.bookvenue.app/api/hotels";

const FALLBACK_HOTEL_IMG =
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=85&auto=format&fit=crop";

/* -------------------------------------------------------
   Helpers
------------------------------------------------------- */

const getHotelImage = (hotel) => {
  return (
    hotel?.image ||
    hotel?.image_url ||
    hotel?.thumbnail ||
    hotel?.cover_image ||
    FALLBACK_HOTEL_IMG
  );
};

const getHotelSlug = (hotel) => {
  return hotel?.slug || hotel?.id || "";
};

const getHotelTags = (hotel) => {
  if (Array.isArray(hotel?.tags)) {
    return hotel.tags;
  }

  if (typeof hotel?.tags === "string") {
    return hotel.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  return [];
};

const getHotelPrice = (hotel) => {
  const price =
    hotel?.price ??
    hotel?.starting_price ??
    hotel?.startingPrice ??
    hotel?.price_per_night ??
    hotel?.pricePerNight;

  if (price === null || price === undefined || price === "") {
    return null;
  }

  const numericPrice = Number(String(price).replace(/[^0-9.]/g, ""));

  return Number.isFinite(numericPrice) ? numericPrice : null;
};

const getHotelRating = (hotel) => {
  const rating = hotel?.rating ?? hotel?.average_rating ?? hotel?.averageRating;

  if (rating === null || rating === undefined || rating === "") {
    return null;
  }

  const numericRating = Number(rating);

  return Number.isFinite(numericRating) ? numericRating : null;
};

/* -------------------------------------------------------
   Component
------------------------------------------------------- */

const Hotels = () => {
  const truncateText = (text, maxLength) => {
    if (!text) return "";

    const value = String(text);

    return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
  };

  const [hotels, setHotels] = useState([]);

  const [sortType, setSortType] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [filterOpen, setFilterOpen] = useState(true);
  const [hotelTypes, setHotelTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [perPage] = useState(12);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 12,
    total: 0,
  });
  const activeHotelType = hotelTypes.find(
    (type) => String(type.id) === String(activeFilter),
  );

  /* -------------------------------------------------------
     Scroll to top
  ------------------------------------------------------- */

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* -------------------------------------------------------
     Fetch Hotels
  ------------------------------------------------------- */

  useEffect(() => {
    const controller = new AbortController();

    const fetchHotels = async () => {
      try {
        setLoading(true);
        setError("");

        const params = new URLSearchParams();

        /*
         * "all" means no type filter.
         */
        if (activeFilter) {
          params.set("type", activeFilter);
        }

        params.set("search", searchQuery.trim());

        params.set("sort", sortType);

        params.set("page", String(page));

        params.set("per_page", String(perPage));

        const url = `${API_URL}?${params.toString()}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Unable to load hotels (${response.status})`);
        }

        const result = await response.json();

        /*
         * Exact API structure:
         *
         * {
         *   data: [],
         *   meta: {
         *     current_page: 1,
         *     last_page: 1,
         *     per_page: 12,
         *     total: 0
         *   }
         * }
         */

        const hotelData = Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result?.data?.data)
            ? result.data.data
            : [];

        setHotels(hotelData);

        setPagination({
          current_page: result?.meta?.current_page || 1,

          last_page: result?.meta?.last_page || 1,

          per_page: result?.meta?.per_page || perPage,

          total: result?.meta?.total || 0,
        });
      } catch (err) {
        if (err?.name === "AbortError") {
          return;
        }

        setHotels([]);

        setError(err?.message || "Something went wrong while loading hotels.");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchHotels();

    return () => {
      controller.abort();
    };
  }, [activeFilter, searchQuery, sortType, page, perPage]);

  /* -------------------------------------------------------
     Reset pagination when filters change
  ------------------------------------------------------- */

  useEffect(() => {
    setPage(1);
  }, [activeFilter, searchQuery, sortType]);

  /* -------------------------------------------------------
     Reveal animation
  ------------------------------------------------------- */

  useEffect(() => {
    const elements = document.querySelectorAll(
      ".ht-page .bv-reveal:not(.is-revealed)",
    );

    if (!("IntersectionObserver" in window) || elements.length === 0) {
      elements.forEach((element) => element.classList.add("is-revealed"));

      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");

            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -40px 0px",
      },
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [hotels, loading, error, activeFilter, sortType, searchQuery, filterOpen]);

  /* -------------------------------------------------------
     Local display filtering
  ------------------------------------------------------- */

  const displayedHotels = useMemo(() => {
    let list = [...hotels];

    /*
     * Local search fallback.
     */
    const query = searchQuery.trim().toLowerCase();

    if (query) {
      list = list.filter((item) => {
        const haystack = [
          item?.name,
          item?.address,
          item?.description,
          item?.type,
          item?.hotel_type,
          ...getHotelTags(item),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(query);
      });
    }

    /*
     * Local sorting fallback.
     */
    if (sortType === "price") {
      list.sort((a, b) => (getHotelPrice(a) || 0) - (getHotelPrice(b) || 0));
    }

    if (sortType === "name") {
      list.sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
      );
    }

    return list;
  }, [hotels, searchQuery, sortType]);

  /* -------------------------------------------------------
     Toolbar status
  ------------------------------------------------------- */

  const toolbarStatus = useMemo(() => {
    const count = displayedHotels.length;

    const query = searchQuery.trim();

    if (loading) {
      return {
        type: "line",
        count: 0,
        text: "loading hotels...",
      };
    }

    if (error) {
      return {
        type: "empty",
        message: "Unable to load hotels",
      };
    }

    if (count === 0) {
      return {
        type: "empty",
        message: query
          ? "No hotels match your search"
          : "No hotels for this type — try another filter",
      };
    }

    if (query) {
      return {
        type: "line",
        count,
        text: `matching “${query}”`,
      };
    }

    if (activeFilter !== "") {
      return {
        type: "line",
        count,
        text: `in ${activeHotelType?.name || "selected type"}`,
      };
    }

    return {
      type: "line",
      count,
      text: "available to book",
    };
  }, [
    displayedHotels.length,
    searchQuery,
    activeFilter,
    activeHotelType,
    loading,
    error,
  ]);

  useEffect(() => {
    const fetchHotelTypes = async () => {
      try {
        setTypesLoading(true);

        const response = await fetch(
          "https://admin.bookvenue.app/api/hotels/types",
          {
            headers: {
              Accept: "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to load hotel types");
        }

        const result = await response.json();

        setHotelTypes(Array.isArray(result?.data) ? result.data : []);
      } catch (error) {
        setHotelTypes([]);
      } finally {
        setTypesLoading(false);
      }
    };

    fetchHotelTypes();
  }, []);

  /* -------------------------------------------------------
     Pagination
  ------------------------------------------------------- */

  const hasPreviousPage = pagination.current_page > 1;

  const hasNextPage = pagination.current_page < pagination.last_page;

  const goToPreviousPage = () => {
    if (hasPreviousPage) {
      setPage(pagination.current_page - 1);

      window.scrollTo({
        top: 500,
        behavior: "smooth",
      });
    }
  };

  const goToNextPage = () => {
    if (hasNextPage) {
      setPage(pagination.current_page + 1);

      window.scrollTo({
        top: 500,
        behavior: "smooth",
      });
    }
  };

  /* -------------------------------------------------------
     Retry
  ------------------------------------------------------- */

  const handleRetry = () => {
    /*
     * Changing page triggers the API effect.
     *
     * If already on page 1, force a tiny state
     * change through the search value.
     */
    setError("");

    if (page !== 1) {
      setPage(1);
      return;
    }

    setSearchQuery((value) => (value === "" ? " " : value));

    setTimeout(() => {
      setSearchQuery((value) => value.trim());
    }, 0);
  };

  /* -------------------------------------------------------
     Render
  ------------------------------------------------------- */

  return (
    <Default>
      <Helmet>
        <title>Hotels in Dehradun | Book Stays | Book Venue</title>

        <meta
          name="description"
          content="Discover luxury resorts, boutique stays and business hotels in Dehradun. Browse and book hotels on Book Venue."
        />

        <meta name="robots" content="index, follow" />

        <link rel="canonical" href="https://bookvenue.app/hotels" />
      </Helmet>

      <div className="af-page ht-page">
        {/* ==================================================
            HERO
        ================================================== */}

        <header className="af-hero">
          <div className="bv-container bv-container--wide af-hero__inner">
            <nav className="af-breadcrumb bv-reveal" aria-label="Breadcrumb">
              <Link to="/" className="af-breadcrumb__link">
                <i className="fa-solid fa-house" aria-hidden="true" />
                Home
              </Link>

              <span className="af-breadcrumb__sep" aria-hidden="true">
                <i className="fa-solid fa-chevron-right" />
              </span>

              <span className="af-breadcrumb__current" aria-current="page">
                Hotels
              </span>
            </nav>

            <div className="af-hero__body">
              <div className="af-hero__content bv-reveal" data-delay="100">
                <span className="af-hero__eyebrow">
                  <i className="fa-solid fa-hotel" aria-hidden="true" />
                  Stays in Doon Valley
                </span>

                <h1 className="af-hero__title">
                  Book hotels in <span className="gradientText">Dehradun</span>
                </h1>

                <p className="af-hero__lede">
                  From peaceful mountain escapes to convenient city stays, find
                  hotels and resorts that match your plans. Explore top stays
                  across Dehradun, check availability, compare options, and book
                  with ease.
                </p>
              </div>

              <div className="af-hero__visual bv-reveal" data-delay="180">
                <LazyImage
                  className="af-hero__img"
                  src={hotelBannerBv}
                  alt="Hotel stay available to book on Book Venue"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </header>

        {/* ==================================================
            LISTINGS
        ================================================== */}

        <section className="af-listings bv-section--cream">
          <div className="bv-container bv-container--wide">
            <div
              className={`af-layout ${
                filterOpen ? "is-filter-open" : "is-filter-collapsed"
              }`}
            >
              {/* ==================================================
                  SIDEBAR
              ================================================== */}

              <aside
                id="ht-type-filters"
                className="af-sidebar bv-reveal bv-reveal--left"
                aria-hidden={!filterOpen}
              >
                <div className="af-sidebar__panel">
                  <div className="af-sidebar__head">
                    <div className="af-sidebar__head-top">
                      <span className="af-sidebar__eyebrow">
                        <i className="fa-solid fa-sliders" aria-hidden="true" />
                        Filters
                      </span>

                      <button
                        type="button"
                        className="af-sidebar__collapse"
                        onClick={() => setFilterOpen(false)}
                        aria-label="Close hotel type filters"
                      >
                        <i
                          className="fa-solid fa-chevron-left"
                          aria-hidden="true"
                        />
                      </button>
                    </div>

                    <h2 className="af-sidebar__title">Hotel type</h2>

                    <p className="af-sidebar__hint">
                      Choose a stay type to see matching hotels
                    </p>
                  </div>

                  <nav
                    className="af-sidebar__filters"
                    role="tablist"
                    aria-label="Filter hotels by type"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeFilter === ""}
                      className={`af-sidebar__filter ${
                        activeFilter === "" ? "is-active" : ""
                      }`}
                      onClick={() => {
                        setActiveFilter("");
                        setPage(1);
                      }}
                    >
                      <span className="af-sidebar__filter-icon bv-hero__chip-icon ht-filter-icon">
                        <i className="fa-solid fa-hotel" aria-hidden="true" />
                      </span>

                      <span className="af-sidebar__filter-text">
                        <span className="af-sidebar__filter-label">
                          All Hotels
                        </span>

                        <span className="af-sidebar__filter-count">
                          {pagination.total} hotels
                        </span>
                      </span>

                      {activeFilter === "" && (
                        <i
                          className="fa-solid fa-check af-sidebar__filter-check"
                          aria-hidden="true"
                        />
                      )}
                    </button>

                    {hotelTypes.map((type) => {
                      const isActive = String(activeFilter) === String(type.id);

                      return (
                        <button
                          key={type.id}
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          className={`af-sidebar__filter ${
                            isActive ? "is-active" : ""
                          }`}
                          onClick={() => {
                            setActiveFilter(String(type.id));
                            setPage(1);
                          }}
                        >
                          <span className="af-sidebar__filter-icon bv-hero__chip-icon ht-filter-icon">
                            <i
                              className="fa-solid fa-hotel"
                              aria-hidden="true"
                            />
                          </span>

                          <span className="af-sidebar__filter-text">
                            <span className="af-sidebar__filter-label">
                              {type.name}
                            </span>

                            <span className="af-sidebar__filter-count">
                              {type.hotels_count} hotel
                              {type.hotels_count === 1 ? "" : "s"}
                            </span>
                          </span>

                          {isActive && (
                            <i
                              className="fa-solid fa-check af-sidebar__filter-check"
                              aria-hidden="true"
                            />
                          )}
                        </button>
                      );
                    })}
                  </nav>

                  <div className="af-sidebar__divider" aria-hidden="true" />

                  {/* SORT */}

                  <div className="af-sidebar__sort-block">
                    <label
                      htmlFor="ht-sortby"
                      className="af-sidebar__sort-label"
                    >
                      <i
                        className="fa-solid fa-arrow-down-wide-short"
                        aria-hidden="true"
                      />
                      Sort results
                    </label>

                    <div className="af-select-wrap">
                      <select
                        id="ht-sortby"
                        className="af-select af-select--sidebar"
                        value={sortType}
                        onChange={(e) => {
                          setSortType(e.target.value);
                          setPage(1);
                        }}
                      >
                        <option value="">Recommended</option>

                        <option value="price">Price: low to high</option>

                        <option value="name">Name: A–Z</option>
                      </select>

                      <i
                        className="fa-solid fa-chevron-down af-select__chevron"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </div>
              </aside>

              {/* ==================================================
                  MAIN
              ================================================== */}

              <div className="af-main">
                {/* TOOLBAR */}

                <div className="af-toolbar bv-reveal">
                  <div className="af-toolbar__left">
                    <button
                      type="button"
                      className={`af-toolbar__filter-toggle ${
                        filterOpen ? "is-open" : ""
                      }`}
                      onClick={() => setFilterOpen((value) => !value)}
                      aria-expanded={filterOpen}
                      aria-controls="ht-type-filters"
                    >
                      <span
                        className="af-toolbar__filter-toggle-icon"
                        aria-hidden="true"
                      >
                        <i className="fa-solid fa-filter" />
                      </span>

                      <span className="af-toolbar__filter-toggle-text">
                        {filterOpen ? "Close filters" : "Hotel filters"}
                      </span>

                      <i
                        className={`fa-solid fa-chevron-${
                          filterOpen ? "left" : "right"
                        } af-toolbar__filter-toggle-chev`}
                        aria-hidden="true"
                      />
                    </button>

                    <div className="af-toolbar__status" aria-live="polite">
                      {toolbarStatus.type === "empty" && (
                        <span className="af-toolbar__status-line af-toolbar__status-line--muted">
                          {toolbarStatus.message}
                        </span>
                      )}

                      {toolbarStatus.type === "line" && (
                        <span className="af-toolbar__status-line">
                          {!loading && (
                            <>
                              <strong>{toolbarStatus.count}</strong>

                              {toolbarStatus.count === 1
                                ? " hotel "
                                : " hotels "}
                            </>
                          )}

                          {toolbarStatus.text}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="af-toolbar__right">
                    {!filterOpen && (
                      <div className="af-toolbar__sort">
                        <label
                          htmlFor="ht-sortby-bar"
                          className="af-toolbar__sort-label"
                        >
                          <i
                            className="fa-solid fa-arrow-down-wide-short"
                            aria-hidden="true"
                          />

                          <span>Sort</span>
                        </label>

                        <div className="af-select-wrap af-select-wrap--toolbar">
                          <select
                            id="ht-sortby-bar"
                            className="af-select af-select--toolbar"
                            value={sortType}
                            onChange={(e) => {
                              setSortType(e.target.value);

                              setPage(1);
                            }}
                          >
                            <option value="">Recommended</option>

                            <option value="price">Price: low to high</option>

                            <option value="name">Name: A–Z</option>
                          </select>

                          <i
                            className="fa-solid fa-chevron-down af-select__chevron"
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                    )}

                    <div className="af-toolbar__search">
                      <i
                        className="fa-solid fa-magnifying-glass"
                        aria-hidden="true"
                      />

                      <input
                        type="search"
                        className="af-toolbar__search-input"
                        placeholder="Search hotels"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);

                          setPage(1);
                        }}
                        aria-label="Search hotels"
                      />

                      {searchQuery && (
                        <button
                          type="button"
                          className="af-toolbar__search-clear"
                          onClick={() => {
                            setSearchQuery("");

                            setPage(1);
                          }}
                          aria-label="Clear search"
                        >
                          <i className="fa-solid fa-xmark" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* ==================================================
                    ERROR
                ================================================== */}

                {!loading && error && (
                  <div className="af-empty bv-reveal">
                    <div className="af-empty__icon" aria-hidden="true">
                      <i className="fa-solid fa-triangle-exclamation" />
                    </div>

                    <h3>Unable to load hotels</h3>

                    <p>{error}</p>

                    <button
                      type="button"
                      className="bv-btn bv-btn--primary"
                      onClick={handleRetry}
                    >
                      Try again
                    </button>
                  </div>
                )}

                {/* ==================================================
                    LOADING
                ================================================== */}

                {loading && !error && (
                  <div className="af-grid">
                    {Array.from({
                      length: 8,
                    }).map((_, index) => (
                      <article
                        key={`hotel-skeleton-${index}`}
                        className="af-venue-card bv-reveal is-revealed"
                      >
                        <div className="af-venue-card__link">
                          <div
                            className="af-venue-card__media"
                            style={{
                              background: "#e8e2d9",
                              minHeight: "240px",
                            }}
                          />

                          <div className="af-venue-card__body">
                            <div
                              style={{
                                height: "22px",
                                width: "70%",
                                background: "#e8e2d9",
                                borderRadius: "6px",
                                marginBottom: "14px",
                              }}
                            />

                            <div
                              style={{
                                height: "15px",
                                width: "90%",
                                background: "#e8e2d9",
                                borderRadius: "6px",
                                marginBottom: "10px",
                              }}
                            />

                            <div
                              style={{
                                height: "15px",
                                width: "80%",
                                background: "#e8e2d9",
                                borderRadius: "6px",
                              }}
                            />
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                )}

                {/* ==================================================
                    HOTEL GRID
                ================================================== */}

                {!loading && !error && displayedHotels.length > 0 && (
                  <div className="af-grid">
                    {displayedHotels.map((item, index) => {
                      const delay = Math.min((index % 6) * 80, 400);

                      const slug = getHotelSlug(item);

                      const image = getHotelImage(item);

                      const price = getHotelPrice(item);

                      const rating = getHotelRating(item);

                      const tags = getHotelTags(item);

                      const name =
                        item?.name ||
                        item?.hotel_name ||
                        item?.hotelName ||
                        item?.title ||
                        "Unnamed Hotel";

                      const address =
                        item?.address ||
                        item?.location ||
                        item?.full_address ||
                        item?.fullAddress ||
                        "Location on request";

                      const description =
                        item?.description ||
                        item?.short_description ||
                        item?.shortDescription ||
                        item?.about ||
                        "Comfortable hotel with flexible booking.";

                      const priceLabel = price
                        ? `₹${price.toLocaleString("en-IN")}/night`
                        : null;

                      return (
                        <article
                          key={`${slug || item?.id || index}-${index}`}
                          className="af-venue-card bv-reveal"
                          data-delay={String(delay)}
                        >
                          <Link
                            to={`/hotels/${slug}`}
                            className="af-venue-card__link"
                          >
                            {/* IMAGE */}

                            <div className="af-venue-card__media">
                              <LazyImage
                                src={image}
                                alt={name}
                                onError={(event) => {
                                  event.currentTarget.src = FALLBACK_HOTEL_IMG;
                                }}
                              />

                              <div
                                className="af-venue-card__media-shine"
                                aria-hidden="true"
                              />

                              <div
                                className="af-venue-card__media-overlay"
                                aria-hidden="true"
                              />

                              <div className="af-venue-card__media-top">
                                {priceLabel && (
                                  <span className="af-venue-card__price">
                                    {priceLabel}
                                  </span>
                                )}

                                {rating !== null && (
                                  <span className="ht-card-rating">
                                    <i
                                      className="fa-solid fa-star"
                                      aria-hidden="true"
                                    />

                                    {rating}
                                  </span>
                                )}

                                <span className="af-venue-card__avail">
                                  <i
                                    className="fa-solid fa-bolt"
                                    aria-hidden="true"
                                  />
                                  Instant book
                                </span>
                              </div>
                            </div>

                            {/* BODY */}

                            <div className="af-venue-card__body">
                              <h3 className="af-venue-card__title">{name}</h3>

                              <p className="af-venue-card__loc">
                                <span className="af-venue-card__loc-icon">
                                  <i
                                    className="fa-solid fa-location-dot"
                                    aria-hidden="true"
                                  />
                                </span>

                                {truncateText(address, 56)}
                              </p>

                              <p className="af-venue-card__desc">
                                {truncateText(description, 72)}
                              </p>

                              {tags.length > 0 && (
                                <div className="af-venue-card__tags">
                                  {tags.slice(0, 3).map((tag, idx) => (
                                    <span
                                      key={`${tag}-${idx}`}
                                      className="af-venue-card__tag bv-hero__chip ht-card-tag"
                                    >
                                      <span className="bv-hero__chip-icon ht-filter-icon">
                                        <i
                                          className="fa-solid fa-check"
                                          aria-hidden="true"
                                        />
                                      </span>

                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}

                              <div className="af-venue-card__footer">
                                <span className="af-venue-card__cta">
                                  View hotel
                                  <span className="af-venue-card__cta-icon">
                                    <i
                                      className="fa-solid fa-arrow-right"
                                      aria-hidden="true"
                                    />
                                  </span>
                                </span>
                              </div>
                            </div>
                          </Link>
                        </article>
                      );
                    })}
                  </div>
                )}

                {/* ==================================================
                    EMPTY STATE
                ================================================== */}

                {!loading && !error && displayedHotels.length === 0 && (
                  <div className="af-empty bv-reveal">
                    <div className="af-empty__icon" aria-hidden="true">
                      <i className="fa-solid fa-magnifying-glass-location" />
                    </div>

                    <h3>No hotels found</h3>

                    <p>
                      {searchQuery
                        ? "Try a different search term."
                        : "There are currently no hotels available for this selection."}
                    </p>

                    {(activeFilter !== "" || searchQuery) && (
                      <button
                        type="button"
                        className="bv-btn bv-btn--primary"
                        onClick={() => {
                          setActiveFilter("");

                          setSearchQuery("");

                          setSortType("");

                          setPage(1);
                        }}
                      >
                        View all hotels
                      </button>
                    )}
                  </div>
                )}

                {/* ==================================================
                    PAGINATION
                ================================================== */}

                {!loading &&
                  !error &&
                  pagination.total > 0 &&
                  pagination.last_page > 1 && (
                    <div
                      className="ht-pagination"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "12px",
                        marginTop: "40px",
                        paddingBottom: "20px",
                      }}
                    >
                      <button
                        type="button"
                        className="bv-btn bv-btn--secondary"
                        disabled={!hasPreviousPage}
                        onClick={goToPreviousPage}
                      >
                        <i className="fa-solid fa-chevron-left" />
                        Previous
                      </button>

                      <span
                        style={{
                          fontWeight: 600,
                          color: "#555",
                        }}
                      >
                        Page {pagination.current_page} of {pagination.last_page}
                      </span>

                      <button
                        type="button"
                        className="bv-btn bv-btn--primary"
                        disabled={!hasNextPage}
                        onClick={goToNextPage}
                      >
                        Next
                        <i className="fa-solid fa-chevron-right" />
                      </button>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </Default>
  );
};

export default Hotels;
